import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { forkJoin } from 'rxjs';
import { ProductivityIncident } from '../../../productivity/productivity.model';
import { ProductivityService } from '../../../productivity/productivity.service';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PosmInstallationTask, PosmProjectStore } from '../../models/posm-execution.models';
import { PosmSetupData } from '../../models/posm-planning.model';
import { PosmProject } from '../../models/posm-project.model';
import { PosmService } from '../../posm.service';

@Component({
  selector: 'app-posm-project-productivity',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageDataPanelComponent,
    PageMetricStripComponent
  ],
  providers: [MessageService],
  templateUrl: './posm-project-productivity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmProjectProductivityComponent implements OnChanges {
  @Input({ required: true }) project!: PosmProject;

  private productivityService = inject(ProductivityService);
  private posmService = inject(PosmService);
  private messageService = inject(MessageService);

  tasks = signal<PosmInstallationTask[]>([]);
  stores = signal<PosmProjectStore[]>([]);
  incidents = signal<ProductivityIncident[]>([]);
  taskTypes = signal<PosmSetupData[]>([]);
  incidentCategories = signal<PosmSetupData[]>([]);
  incidentSeverities = signal<PosmSetupData[]>([]);
  loading = signal(false);

  taskDraft: { storeId?: number | null; taskType?: string; status?: string } = { status: 'PENDING' };
  incidentDraft: ProductivityIncident = { title: '', status: 'OPEN' };

  taskTypeOptions = computed(() => this.toOptions(this.taskTypes()));
  incidentCategoryOptions = computed(() => this.toOptions(this.incidentCategories()));
  severityOptions = computed(() => this.toOptions(this.incidentSeverities()));

  storeOptions = computed(() => this.stores().map(store => ({
    label: store.location?.name || store.storeNumber || `Store #${store.id}`,
    value: store.id
  })));

  metrics = computed<PageMetric[]>(() => [
    {
      label: 'Open Tasks',
      value: this.tasks().filter(task => !this.isCompleted(task.status)).length,
      hint: 'Store execution workload',
      tone: 'info'
    },
    {
      label: 'Released',
      value: this.tasks().filter(task => task.transportStatus === 'RELEASED_TO_SHIPMENT').length,
      hint: 'Converted to shipments',
      tone: 'success'
    },
    {
      label: 'Open Incidents',
      value: this.incidents().filter(incident => this.isOpen(incident.status)).length,
      hint: 'Client and execution issues',
      tone: 'danger'
    },
    {
      label: 'Resolved',
      value: this.incidents().filter(incident => !this.isOpen(incident.status)).length,
      hint: 'Closed incident records',
      tone: 'success'
    }
  ]);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project'] && this.project?.id) {
      this.load();
    }
  }

  load(): void {
    const projectId = this.project.id;
    const companyId = this.project.owningCompanyId || this.productivityService.getCompanyId();
    if (!projectId || !companyId) return;
    this.loading.set(true);
    forkJoin({
      tasks: this.posmService.getExecutionTasksByProject(projectId, companyId),
      stores: this.posmService.getStoresByProject(projectId, companyId),
      incidents: this.productivityService.getIncidents({ resourceKey: 'POSM_PROJECT', resourceId: projectId }),
      taskTypes: this.posmService.getSetup('TASK_TYPE', companyId),
      incidentCategories: this.posmService.getSetup('INCIDENT_CATEGORY', companyId),
      incidentSeverities: this.posmService.getSetup('INCIDENT_SEVERITY', companyId)
    }).subscribe({
      next: ({ tasks, stores, incidents, taskTypes, incidentCategories, incidentSeverities }) => {
        this.tasks.set(tasks || []);
        this.stores.set(stores || []);
        this.incidents.set(incidents.data || []);
        this.taskTypes.set(taskTypes || []);
        this.incidentCategories.set(incidentCategories || []);
        this.incidentSeverities.set(incidentSeverities || []);
        this.taskDraft.taskType ||= taskTypes?.[0]?.code;
        this.incidentDraft.category ||= incidentCategories?.[0]?.code;
        this.incidentDraft.severity ||= incidentSeverities?.[0]?.code;
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({ severity: 'error', summary: 'POSM Workload', detail: 'Unable to load tasks and incidents.' });
      }
    });
  }

  saveTask(): void {
    const projectId = this.project.id;
    const companyId = this.project.owningCompanyId || this.productivityService.getCompanyId();
    if (!this.taskDraft.storeId || !this.taskDraft.taskType || !projectId || !companyId) return;
    this.posmService.saveInstallationTask({
      projectId,
      owningCompanyId: companyId,
      store: { id: this.taskDraft.storeId },
      taskType: this.taskDraft.taskType,
      status: this.taskDraft.status || 'PENDING'
    }).subscribe({
      next: () => {
        this.taskDraft = { taskType: this.taskTypes()[0]?.code, status: 'PENDING' };
        this.messageService.add({ severity: 'success', summary: 'Task Saved', detail: 'POSM store task was added.' });
        this.load();
      }
    });
  }

  saveIncident(): void {
    if (!this.incidentDraft.title?.trim() || !this.incidentDraft.category || !this.project.id) return;
    this.productivityService.saveIncident({
      ...this.incidentDraft,
      title: this.incidentDraft.title.trim(),
      resourceKey: 'POSM_PROJECT',
      resourceId: this.project.id,
      ownerCustomerId: this.incidentDraft.ownerCustomerId || this.productivityService.getCustomerId()
    }).subscribe({
      next: () => {
        this.incidentDraft = {
          title: '',
          category: this.incidentCategories()[0]?.code,
          severity: this.incidentSeverities()[0]?.code,
          status: 'OPEN'
        };
        this.messageService.add({ severity: 'success', summary: 'Incident Saved', detail: 'POSM incident was added.' });
        this.load();
      }
    });
  }

  resolveIncident(incident: ProductivityIncident): void {
    if (!incident.id) return;
    this.productivityService.resolveIncident(incident.id, incident.resolutionNotes || 'Resolved from POSM workspace.').subscribe(() => this.load());
  }

  storeName(task: PosmInstallationTask): string {
    return task.store?.location?.name || task.storeName || task.store?.storeNumber || '-';
  }

  statusSeverity(status?: string) {
    if (this.isCompleted(status)) return 'success';
    if (status === 'ASSIGNED_TO_TRIP' || status === 'RELEASE_READY') return 'info';
    if (status === 'VALIDATION_ERROR') return 'danger';
    return 'warn';
  }

  transportSeverity(status?: string) {
    if (status === 'RELEASED_TO_SHIPMENT') return 'success';
    if (status === 'RELEASE_READY') return 'info';
    if (status === 'VALIDATION_ERROR') return 'danger';
    return 'secondary';
  }

  incidentSeverity(severity?: string) {
    switch (severity) {
      case 'CRITICAL':
      case 'HIGH':
        return 'danger';
      case 'LOW':
        return 'secondary';
      default:
        return 'warn';
    }
  }

  incidentStatusSeverity(status?: string) {
    if (!this.isOpen(status)) return 'success';
    return status === 'IN_PROGRESS' ? 'info' : 'warn';
  }

  private isCompleted(status?: string): boolean {
    return ['COMPLETED', 'CLOSED', 'CANCELLED'].includes(status || '');
  }

  private isOpen(status?: string): boolean {
    return !status || ['OPEN', 'IN_PROGRESS', 'PENDING'].includes(status);
  }

  private toOptions(rows: PosmSetupData[]) {
    return rows
      .filter(row => row.isActive !== false)
      .map(row => ({ label: row.name, value: row.code || row.name }));
  }
}
